import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Heart, 
  Shield, 
  Award, 
  Users,
  MapPin,
  Phone,
  Star,
  CheckCircle
} from 'lucide-react';
import { hospitalInfo } from '../mock';
import { useBusinessInfo } from '../hooks/useBusinessInfo';
import InnerPageLayout from '../components/InnerPageLayout';

const OurCoreValues = () => {
  const { businessInfo: currentBusinessInfo } = useBusinessInfo();
  const primaryColor = 'var(--theme-primary)';
  const primaryLight = 'var(--theme-secondary)';
  const primaryBg = 'var(--theme-primary-light)';

  const values = [
    {
      icon: Heart,
      title: "Compassionate Care",
      description: "We treat every pet with the same love and attention we would give our own family members.",
      color: "#ef4444"
    },
    {
      icon: Shield,
      title: "Quality Medicine",
      description: "Using the latest medical technology and proven treatment methods for the best outcomes.",
      color: "#10b981"
    },
    {
      icon: Users,
      title: "Collaborative Approach",
      description: "We work closely with pet owners to develop the best care plans for each individual pet.",
      color: "#3b82f6"
    },
    {
      icon: Award,
      title: "Experienced Team",
      description: "Our veterinarians and staff bring years of experience and continuing education to every case.",
      color: "#f59e0b"
    }
  ];

  const valueDetails = [
    {
      title: "Patient-Centered Care",
      description: "Every decision we make is based on what's best for your pet's health and wellbeing",
      icon: Heart
    },
    {
      title: "Continuous Learning",
      description: "Our team stays current with the latest veterinary advances and best practices",
      icon: Star
    },
    {
      title: "Transparent Communication",
      description: "We believe in keeping pet owners informed every step of the way",
      icon: CheckCircle
    }
  ];

  // Hero content for the left side
  const heroContent = (
    <div className="text-center lg:text-left">
      <h1 className="text-xl font-bold text-gray-900 mb-4">
        Our Values
      </h1>
      <div className="mb-4">
        <span className="inline-flex items-center px-4 py-2 rounded-md text-sm font-semibold text-white" style={{ backgroundColor: primaryColor }}>
          Compassionate • Professional • Trustworthy
        </span>
      </div>
      <p className="text-base text-gray-600 leading-relaxed">
        These core values shape every interaction, decision, and treatment plan at our hospital.
      </p>
    </div>
  );

  return (
    <InnerPageLayout heroContent={heroContent}>
    <div className="bg-gray-50">
      {/* Core Values Grid */}
      <section className="bg-[#f8f9fa] py-6">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {values.map((value, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md">
                <div className="flex items-center mb-4">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full mr-4" style={{ backgroundColor: `${value.color}15` }}>
                    <value.icon className="h-6 w-6" style={{ color: value.color }} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">{value.title}</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values in Action */}
      <section className="bg-white py-6">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-bold text-gray-900 mb-8 text-center">
            Our Values in Action
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {valueDetails.map((detail, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-xl shadow-md text-center">
                <div className="flex items-center justify-center w-12 h-12 rounded-full mx-auto mb-4" style={{ backgroundColor: `${primaryColor}15` }}>
                  <detail.icon className="h-6 w-6" style={{ color: primaryColor }} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{detail.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{detail.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Promise */}
      <section className="bg-[#f8f9fa] py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white p-8 rounded-xl border-l-4" style={{ borderColor: primaryColor }}>
            <div className="flex items-center justify-center mb-4">
              <CheckCircle className="h-6 w-6 mr-3" style={{ color: primaryColor }} />
              <h3 className="text-xl font-bold text-gray-900">Our Promise to You</h3>
            </div>
            <p className="text-gray-800 leading-relaxed mb-4 text-center">
              When you choose Pets & Vets Animal Hospital, you're choosing a team that lives by these values every single day. 
              From the moment you walk through our doors to the ongoing care of your beloved pet, these principles guide our actions 
              and decisions.
            </p>
            <div className="flex items-center justify-center">
              <Heart className="h-5 w-5 mr-2 text-red-500" />
              <span className="text-gray-800 font-medium">Compassionate • Professional • Trustworthy • Experienced</span>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section style={{ background: `linear-gradient(135deg, ${primaryColor} 0%, var(--theme-primary-hover) 100%)`, paddingTop: '30px', paddingBottom: '30px' }}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-bold text-white mb-4" style={{ fontSize: '1rem' }}>
            Experience Our Values Firsthand
          </h2>
          <p className="mb-8" style={{ color: 'white', fontSize: '1rem' }}>
            See how our core values translate into exceptional care for your pet
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`tel:${currentBusinessInfo.phone}`}
              className="inline-flex items-center justify-center bg-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
              style={{ color: primaryColor }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
            >
              <Phone className="mr-2 h-5 w-5" />
              Call Now: {currentBusinessInfo.phone}
            </a>
            <Link
              to="/reach-us"
              className="inline-flex items-center justify-center border-2 border-white text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'white';
                e.target.style.color = primaryColor;
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';  
                e.target.style.color = 'white';
              }}
            >
              Schedule Visit
              <MapPin className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
    </InnerPageLayout>
  );
};

export default OurCoreValues;